import { useState, useEffect } from 'react';
import { useAtomValue } from 'jotai';
import { userInfoAtom } from '@/data/atoms/utilsAtoms';
import CloseIcon from '@mui/icons-material/Close';
import Toast from '@/utils/toastManager';
import { PORTAL_STATUS } from '@/Pages/admin/ChargebeePortal/constants/portalUiConstants';

const FailuresAlerts = () => {
  const [failures, setFailures] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const userData = useAtomValue(userInfoAtom);

  useEffect(() => {
    fetchFailures();
  }, []);

  const fetchFailures = async () => {
    setLoading(true);
    await fetch(
      `${import.meta.env.VITE_APP_API_URL}/portal/failures`,
      {
        headers: { Authorization: `Bearer ${userData.token}` },
        cache: 'no-store'
      }
    )
      .then(async (response) => {
        if (!response.ok) {
          Toast.error('Unable to refresh failures and alerts');
          return;
        }

        const data = await response.json();
        setFailures(data.data);
        Toast.success('Failures and alerts refreshed');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleReprocess = async (eventId) => {
    await fetch(
      `${import.meta.env.VITE_APP_API_URL}/portal/webhooks/${eventId}/reprocess`,
      {
        method: 'POST',
        headers: { Authorization: `Bearer ${userData.token}` }
      }
    );
    Toast.success('Webhook reprocessed successfully');
    setSelectedEvent(null);
    fetchFailures();
  };

  const handleForceLink = async (eventId) => {
    await fetch(
      `${import.meta.env.VITE_APP_API_URL}/portal/webhooks/${eventId}/force-link`,
      {
        method: 'POST',
        headers: { Authorization: `Bearer ${userData.token}` }
      }
    );
    Toast.success('Webhook force linked successfully');
    setSelectedEvent(null);
    fetchFailures();
  };

  if (loading && !failures) {
    return <div className="loading">Loading failures and alerts...</div>;
  }

  if (!failures) {
    return <div className="error">Failed to load failures data</div>;
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
        <button className="btn btn-primary" onClick={fetchFailures} disabled={loading}>
          {loading ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>
      <div className="alerts-grid">
        <div className="alert-card danger">
          <div className="alert-value">{failures.failedWebhooks24h}</div>
          <div className="alert-label">Failed Webhooks (24h)</div>
        </div>
        <div className="alert-card warning">
          <div className="alert-value">{failures.unlinkedEvents}</div>
          <div className="alert-label">Unlinked Events</div>
        </div>
        <div className="alert-card warning">
          <div className="alert-value">{failures.pendingProcessing}</div>
          <div className="alert-label">Pending Processing</div>
        </div>
      </div>

      <div className="failures-section">
        <div className="section-title">Recent Failures</div>
        {failures.recentFailures?.length > 0 ? (
          failures.recentFailures.map((failure) => (
            <div key={failure.id} className="failure-item" onClick={() => setSelectedEvent(failure)} style={{ cursor: 'pointer' }}>
              <div className="failure-header">
                <div className="failure-type">{failure.eventType}</div>
                <div className="failure-time">
                  {failure.timestamp ? new Date(failure.timestamp).toLocaleString() : 'N/A'}
                </div>
              </div>
              <div className="webhook-meta">
                <span>Event ID: {failure.eventId}</span>
                {failure.customerId && <span>Customer ID: {failure.customerId}</span>}
                {failure.userEmail && <span>Email: {failure.userEmail}</span>}
                {failure.userId && <span>User: {failure.userId}</span>}
              </div>
              {failure.error && (
                <div className="failure-error">{failure.error}</div>
              )}
            </div>
          ))
        ) : (
          <div className="empty-state">
            <div className="empty-state-icon">✓</div>
            <p>No recent failures</p>
          </div>
        )}
      </div>

      <div className="failures-section">
        <div className="section-title">Unlinked Events</div>
        {failures.unlinkedEventsList?.length > 0 ? (
          failures.unlinkedEventsList.map((event) => (
            <div key={event.eventId} className="unlinked-item" onClick={() => setSelectedEvent(event)} style={{ cursor: 'pointer' }}>
              <div className="unlinked-header">
                <div className="unlinked-type">{event.eventType}</div>
                <div className="failure-time">
                  {event.timestamp ? new Date(event.timestamp).toLocaleString() : 'N/A'}
                </div>
              </div>
              <div className="webhook-meta">
                <span>Event ID: {event.eventId}</span>
                {event.customerId && <span>Customer ID: {event.customerId}</span>}
                {event.userEmail && <span>Email: {event.userEmail}</span>}
              </div>
              {event.reason && (
                <div style={{ marginTop: '0.5rem', fontSize: '0.88rem', color: '#92400e' }}>
                  Reason: {event.reason}
                </div>
              )}
            </div>
          ))
        ) : (
          <div className="empty-state">
            <p>No unlinked events</p>
          </div>
        )}
      </div>

      {selectedEvent && (
        <div className="modal-overlay" onClick={() => setSelectedEvent(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Event Details</h3>
              <button className="modal-close" onClick={() => setSelectedEvent(null)}>
                <CloseIcon />
              </button>
            </div>

            <div className="payload-section">
              <h4>Event Information</h4>
              <div className="webhook-meta" style={{ marginBottom: '1rem', display: 'block' }}>
                <div className="meta-item"><strong>Type:</strong> {selectedEvent.eventType}</div>
                <div className="meta-item"><strong>Status:</strong> {selectedEvent.status}</div>
                <div className="meta-item"><strong>Event ID:</strong> {selectedEvent.eventId}</div>
                {selectedEvent.customerId && <div className="meta-item"><strong>Customer ID:</strong> {selectedEvent.customerId}</div>}
                {selectedEvent.userEmail && <div className="meta-item"><strong>Email:</strong> {selectedEvent.userEmail}</div>}
                <div className="meta-item"><strong>Time:</strong> {selectedEvent.timestamp ? new Date(selectedEvent.timestamp).toLocaleString() : 'N/A'}</div>
              </div>
            </div>

            {selectedEvent.webhookPayload && (
              <div className="payload-section">
                <h4>Payload</h4>
                <div className="payload-content">
                  {JSON.stringify(selectedEvent.webhookPayload, null, 2)}
                </div>
              </div>
            )}

            {selectedEvent.error && (
              <div className="payload-section">
                <h4>Error</h4>
                <div className="payload-content" style={{ color: '#ef4444' }}>
                  {selectedEvent.error}
                </div>
              </div>
            )}

            <div className="webhook-actions" style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '0.07rem solid #e5e7eb' }}>
              {selectedEvent.status === PORTAL_STATUS.FAILED && (
                <button
                  className="btn btn-primary"
                  onClick={() => handleReprocess(selectedEvent.eventId)}
                >
                  Reprocess
                </button>
              )}
              {!selectedEvent.userId && (
                <button
                  className="btn btn-primary"
                  onClick={() => handleForceLink(selectedEvent.eventId)}
                >
                  Force Link
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FailuresAlerts;